import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { Modal } from './Modal';
import { Button } from './Button';
import { useColors, ThemeColors } from '../../hooks/useColors';
import { spacing } from '../../theme';
import { useGuestMode } from '../../contexts/GuestModeContext';

interface AuthRequiredModalProps {
  visible?: boolean;
  onClose?: () => void;
  featureName?: string;
}

export function AuthRequiredModal({ visible, onClose, featureName }: AuthRequiredModalProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<any>();
  const { showAuthModal, setShowAuthModal, pendingFeature, setGuestMode } = useGuestMode();
  
  // Use props if provided, otherwise use context
  const isVisible = visible !== undefined ? visible : showAuthModal;
  const feature = featureName || pendingFeature;
  
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setShowAuthModal(false);
    }
  };
  
  const handleSignIn = () => {
    handleClose();
    // Clear guest mode when navigating to auth
    setGuestMode(false);
    navigation.navigate('Auth');
  };
  
  const getFeatureMessage = () => {
    if (!feature) {
      return 'Sign in to access all features';
    }
    return `Sign in to ${feature}`;
  };

  return (
    <Modal visible={isVisible} onClose={handleClose}>
      <View style={styles.modalContent}>
        <View style={styles.iconContainer}>
          <Feather name="user" size={28} color={colors.primary} />
        </View>
        
        <Text style={styles.title}>Sign In Required</Text>
        <Text style={styles.description}>{getFeatureMessage()}</Text>
        
        <View style={styles.buttonsContainer}>
          <Button 
            onPress={handleSignIn} 
            style={styles.signInButton}
          >
            Sign In
          </Button>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  modalContent: {
    padding: spacing.xl,
    paddingTop: spacing.md,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 22,
    backgroundColor: colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  title: {
    fontSize: 24,
    fontWeight: '800',
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.xs,
    letterSpacing: -0.5,
  },
  description: {
    fontSize: 16,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
    lineHeight: 22,
  },
  buttonsContainer: {
    gap: spacing.sm,
  },
  signInButton: {
    height: 56,
    borderRadius: 14,
  },
});

export default AuthRequiredModal;
